import { useEffect, useMemo, useRef, useState } from 'react';

import { confirmTransaction } from 'Services/privateApi';

type ConfirmSale = {
  closingBidId: number;
  enquiryId: number;
}

export const useConfirmSale = ({
  onError,
}: {
  onError?: (error?: unknown) => void;
} = {}) => {
  const [isLoading, setIsLoading] = useState(false);

  const abortController = useMemo(() => new AbortController(), []);
  const cancelled = useRef(false);

  useEffect(() => () => {
    cancelled.current = true;
  }, [abortController]);

  const confirmSale = async ({ closingBidId, enquiryId }: ConfirmSale) => {
    setIsLoading(true);

    if (!enquiryId && !closingBidId) {
      onError?.();
      setIsLoading(false);
      return;
    }

    try {
      await confirmTransaction(enquiryId);

      if (!cancelled?.current) {
        setIsLoading(false);
      }
    } catch (error) {
      if (cancelled?.current) {
        return;
      }

      window.Sentry?.captureException(new Error('Error confirming sale', {
        cause: error,
      }), {
        extra: {
          closingBidId,
          enquiryId,
        },
        tags: {
          page: 'Offer summary',
        },
      });

      setIsLoading(false);
      onError?.(error);
    } finally {
      if (!cancelled?.current) {
        setIsLoading(false);
      }
    }
  };

  return {
    confirmSale,
    isLoading,
  };
};
